import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Clock, 
  Gauge, 
  Mountain, 
  Footprints, 
  Calendar,
  Eye,
  EyeOff,
  Search,
  Filter,
  MoreVertical,
  Trash2,
  Edit,
  Share2,
  Download,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const SavedWalksPage = () => {
  const { user } = useAuth();
  const [savedWalks, setSavedWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPrivacy, setFilterPrivacy] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (user) {
      fetchSavedWalks();
    }
  }, [user]);

  const fetchSavedWalks = async () => {
    try {
      const token = localStorage.getItem('cms_token') || localStorage.getItem('authToken');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/favorites/walks`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Normalize the response to the shape the page expects
      const walks = (response.data?.walks || []).map(w => ({
        ...w,
        id: w.id,
        title: w.title,
        description: w.description || w.practical_summary || '',
        neighborhood: w.region,
        stats: {
          distance_meters: w.distance_meters || 0,
          duration_seconds: (w.duration_minutes || 0) * 60,
        },
        privacy: 'public',
        tags: w.categories || [],
      }));
      setSavedWalks(walks);
    } catch (error) {
      console.error('Error fetching saved walks:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${Math.round(meters)} m`;
  };

  const formatSpeed = (ms) => {
    const kmh = ms * 3.6;
    return `${kmh.toFixed(1)} km/h`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'public':
        return <Eye className="h-4 w-4 text-green-500" />;
      case 'friends':
        return <Eye className="h-4 w-4 text-blue-500" />;
      default:
        return <EyeOff className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPrivacyColor = (privacy) => {
    switch (privacy) {
      case 'public':
        return 'bg-green-100 text-green-800';
      case 'friends':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter and sort walks
  const filteredWalks = savedWalks
    .filter(walk => {
      const matchesSearch = walk.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           walk.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           walk.neighborhood?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPrivacy = filterPrivacy === 'all' || walk.privacy === filterPrivacy;
      
      return matchesSearch && matchesPrivacy;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'distance':
          return b.stats.distance_meters - a.stats.distance_meters;
        case 'duration':
          return b.stats.duration_seconds - a.stats.duration_seconds;
        default:
          return 0;
      }
    });

  const getThumbnailImage = (walk) => {
    // Return first photo if available, otherwise use a map-based thumbnail
    if (walk.photos && walk.photos.length > 0) {
      return walk.photos[0].data;
    }
    
    // Create a simple map thumbnail using coordinates
    if (walk.coordinates && walk.coordinates.length > 0) {
      const center = walk.coordinates[Math.floor(walk.coordinates.length / 2)];
      return `https://maps.googleapis.com/maps/api/staticmap?center=${center.lat},${center.lng}&zoom=14&size=300x200&path=color:0x3B82F6|weight:3|${walk.coordinates.map(c => `${c.lat},${c.lng}`).join('|')}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
    }
    
    return null;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">My Saved Walks</h1>
          <p className="text-gray-600 mb-8">Please log in to view your saved GPS tracked walks</p>
          <Button>
            <Link to="/membership">Get Started</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Saved Walks</h1>
            <p className="text-gray-600">
              Your GPS tracked walks and personal walking journal
            </p>
          </div>
          
          <div className="flex space-x-3 mt-4 sm:mt-0">
            <Button asChild>
              <Link to="/tracking/live">
                <Play className="h-4 w-4 mr-2" />
                Start New Walk
              </Link>
            </Button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search walks by title, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Privacy Filter */}
            <div className="sm:w-48">
              <select
                value={filterPrivacy}
                onChange={(e) => setFilterPrivacy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Privacy Levels</option>
                <option value="private">Private Only</option>
                <option value="friends">Friends Only</option>
                <option value="public">Public Only</option>
              </select>
            </div>
            
            {/* Sort */}
            <div className="sm:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="distance">Longest Distance</option>
                <option value="duration">Longest Duration</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {filteredWalks.length} of {savedWalks.length} walks
          </p>
        </div>

        {/* Walks Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <Card key={index} className="animate-pulse">
                <div className="h-48 bg-gray-200 rounded-t-lg"></div>
                <CardContent className="p-6">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-4 w-3/4"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredWalks.length === 0 ? (
          <div className="text-center py-16">
            <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {savedWalks.length === 0 ? 'No walks yet' : 'No walks match your filters'}
            </h3>
            <p className="text-gray-600 mb-6">
              {savedWalks.length === 0 
                ? 'Start tracking your first walk to build your personal collection'
                : 'Try adjusting your search or filter criteria'
              }
            </p>
            {savedWalks.length === 0 && (
              <Button asChild>
                <Link to="/tracking/live">
                  <Play className="h-4 w-4 mr-2" />
                  Start Your First Walk
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWalks.map((walk) => (
              <Card key={walk.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* Walk Thumbnail */}
                <div className="relative h-48 bg-gray-200">
                  {getThumbnailImage(walk) ? (
                    <img
                      src={getThumbnailImage(walk)}
                      alt={walk.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <MapPin className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Privacy Badge */}
                  <div className="absolute top-3 left-3">
                    <Badge className={`${getPrivacyColor(walk.privacy)} capitalize`}>
                      <span className="mr-1">{getPrivacyIcon(walk.privacy)}</span>
                      {walk.privacy}
                    </Badge>
                  </div>
                  
                  {/* Duration Badge */}
                  <div className="absolute top-3 right-3">
                    <Badge variant="secondary" className="bg-black/70 text-white">
                      {formatDuration(walk.stats.duration_seconds)}
                    </Badge>
                  </div>
                </div>

                {/* Walk Info */}
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">
                      {walk.title}
                    </h3>
                    {walk.description && (
                      <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                        {walk.description}
                      </p>
                    )}
                    {walk.neighborhood && (
                      <p className="text-blue-600 text-sm font-medium">
                        {walk.neighborhood}
                      </p>
                    )}
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 text-blue-500 mr-2" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {formatDistance(walk.stats.distance_meters)}
                        </div>
                        <div className="text-xs text-gray-500">Distance</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Gauge className="h-4 w-4 text-purple-500 mr-2" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {formatSpeed(walk.stats.average_speed_ms)}
                        </div>
                        <div className="text-xs text-gray-500">Avg Speed</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Mountain className="h-4 w-4 text-orange-500 mr-2" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {Math.round(walk.stats.elevation_gain_meters)}m
                        </div>
                        <div className="text-xs text-gray-500">Elevation</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Footprints className="h-4 w-4 text-red-500 mr-2" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {walk.stats.calories_burned}
                        </div>
                        <div className="text-xs text-gray-500">Calories</div>
                      </div>
                    </div>
                  </div>

                  {/* Date and Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center text-gray-500 text-sm">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(walk.created_at)}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // TODO: Implement view walk details
                          console.log('View walk:', walk.id);
                        }}
                      >
                        View
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          // TODO: Implement walk actions menu
                          console.log('Walk actions:', walk.id);
                        }}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Tags */}
                  {walk.tags && walk.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {walk.tags.slice(0, 3).map((tag, index) => (
                        <Badge 
                          key={index} 
                          variant="outline" 
                          className="text-xs"
                        >
                          #{tag}
                        </Badge>
                      ))}
                      {walk.tags.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{walk.tags.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Load More Button (if needed for pagination) */}
        {filteredWalks.length > 0 && filteredWalks.length % 20 === 0 && (
          <div className="text-center mt-8">
            <Button variant="outline">
              Load More Walks
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedWalksPage;